import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ApiError } from '../api/client';
import { getAuthMe, login as loginRequest, logoutRequest } from '../api/endpoints';
import { queryKeys } from '../api/queryKeys';
import type { AuthUser } from '../api/types';
import { adminQueryClient } from '../app/queryClient';
import { clearAdminWriteToken } from '../pages/admin/adminWriteToken';
import { localDatabaseExistsForUser } from '../sync/db';
import { useOnline } from '../sync/online';
import { loginOneSignal, logoutOneSignal, requestPushPermission } from '../native/bridge';
import { runningEnv } from '../env/appEnv';
import {
  hasLoggedInBefore,
  isProactivePushEnabled,
  isStartupPushPromptDone,
  markLoggedInBefore,
  markStartupPushPromptDone,
} from '../native/pushPreferences';
import {
  clearCachedAuthUserSnapshot,
  clearStoredAuthToken,
  readCachedAuthUserSnapshot,
  setStoredAuthToken,
  writeCachedAuthUserSnapshot,
} from './storage';
import { setAiDisclosureAckScope } from '../components/chat/aiDisclosure';
import { markExplicitLogoutPending } from './logoutSignal';

// W1-B: 'anonymous' = gar nicht angemeldet (frueher 'guest'). Umbenannt, weil
// „Gast" seit den zwei Einstiegen ein echtes, angemeldetes Konto bezeichnet
// (users.is_guest) — ein Gastkonto hat hier den Status 'authed'.
export type AuthStatus = 'loading' | 'authed' | 'anonymous';

export interface AuthContextValue {
  user: AuthUser | null;
  status: AuthStatus;
  offline: boolean;
  /** Letzter /api/auth/me-Fehler — fuer die Debug-Diagnostik im Fehler-Screen. */
  lastError: unknown;
  /** Nur nach einer vom User explizit ausgeloesten Logout-Aktion gesetzt. */
  logoutRequested: boolean;
  /** Der Koordinator darf den vorgeschalteten App-Push-Hinweis anzeigen. */
  startupPushPermissionPending: boolean;
  refresh: () => Promise<void>;
  login: (identifier: string, password: string, remember?: boolean) => Promise<AuthUser>;
  logout: () => Promise<void>;
  dismissStartupPushPermission: () => void;
  requestStartupPushPermission: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function isAuthRejection(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 403);
}

const OWN_SERVER_AUTH_ERROR_CODES = new Set([
  'auth_not_authenticated',
  'auth_session_expired',
  'auth_account_disabled',
  'auth_user_not_found',
]);

export function shouldWipeStoredBearer(error: unknown): boolean {
  if (!(error instanceof ApiError)
      || (error.status !== 401 && error.status !== 403)
      || !error.hadBearerAuthorization) return false;
  if (!error.data || typeof error.data !== 'object') return false;
  const data = error.data as {
    auth_error?: unknown;
    auth_transport?: unknown;
    code?: unknown;
    error?: unknown;
  };
  return data.auth_error === true
    && data.auth_transport === 'bearer'
    && typeof data.error === 'string'
    && OWN_SERVER_AUTH_ERROR_CODES.has(String(data.code));
}

function isNetworkFailure(error: unknown): boolean {
  if (error instanceof ApiError) return false;
  if (error instanceof TypeError) return true;
  const name = error && typeof error === 'object' && 'name' in error
    ? String((error as { name?: unknown }).name)
    : '';
  if (name === 'NetworkError') return true;
  const message = error instanceof Error
    ? error.message.toLowerCase()
    : typeof error === 'string'
      ? error.toLowerCase()
      : '';
  return message.includes('failed to fetch')
    || message.includes('networkerror')
    || message.includes('network request failed')
    || message.includes('load failed');
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const [offline, setOffline] = useState(false);
  const [logoutRequested, setLogoutRequested] = useState(false);
  const [startupPushPermissionPending, setStartupPushPermissionPending] = useState(false);
  const fetchAuthMe = useCallback(async (): Promise<AuthUser> => {
    try {
      const user = await getAuthMe();
      if (typeof user.token === 'string' && user.token.trim()) {
        // Der Server sendet diesen Wert nur beim nativen Bearer-Renewal. Web
        // bleibt beim HttpOnly-Cookie und legt weiterhin keinen JS-Token ab.
        await setStoredAuthToken(user.token);
      }
      writeCachedAuthUserSnapshot(user);
      setOffline(false);
      return user;
    } catch (error) {
      if (isAuthRejection(error)) {
        clearCachedAuthUserSnapshot();
        // DA-Fix R2: Nur ein nachweislich mit Bearer gesendetes /me, das mit
        // unserer markierten Server-Fehlerform antwortet, darf den gespeicherten
        // Token loeschen. Native Boots ohne Token sowie Proxy-/WAF-401
        // bleiben dadurch zerstoerungsfrei; ein wirklich toter Bearer wird
        // weiterhin aus der Startschleife entfernt.
        if (shouldWipeStoredBearer(error)) {
          void clearStoredAuthToken().catch(() => undefined);
        }
        setOffline(false);
        throw error;
      }

      const cachedUser = readCachedAuthUserSnapshot();
      if (isNetworkFailure(error) && cachedUser) {
        const hasLocalDb = await localDatabaseExistsForUser(cachedUser.userId).catch(() => false);
        if (hasLocalDb) {
          setOffline(true);
          return cachedUser;
        }
      }

      setOffline(false);
      throw error;
    }
  }, []);
  const meQuery = useQuery({
    queryKey: queryKeys.auth.me,
    queryFn: fetchAuthMe,
    // 401/403 = endgueltig (anonymous). Alles andere (Netzwerk, 5xx, CORS-Block)
    // ist transient: mit Backoff wiederholen UND danach im 10s-Takt weiter
    // probieren — sonst friert ein einziger Fehlversuch beim App-Start den
    // Status dauerhaft auf 'loading' ein (Android-APK-Haenger 2026-06-10).
    retry: (failureCount, error) => !isAuthRejection(error) && failureCount < 4,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    refetchInterval: (query) =>
      query.state.status === 'error' && !isAuthRejection(query.state.error) ? 10_000 : false,
    staleTime: 60_000,
  });
  const refetchMe = meQuery.refetch;

  useEffect(() => {
    if (!online || !offline) return;
    void refetchMe();
  }, [offline, online, refetchMe]);

  const refresh = useCallback(async () => {
    await refetchMe();
  }, [refetchMe]);

  useEffect(() => {
    const completed = () => { void refresh(); };
    window.addEventListener('lorath:native-oauth-complete', completed);
    return () => window.removeEventListener('lorath:native-oauth-complete', completed);
  }, [refresh]);

  const login = useCallback(async (identifier: string, password: string, remember?: boolean) => {
    const response = await loginRequest(identifier, password, remember);
    if (typeof response.token === 'string' && response.token) {
      await setStoredAuthToken(response.token);
    }
    const user = await queryClient.fetchQuery({
      queryKey: queryKeys.auth.me,
      queryFn: async () => {
        const nextUser = await getAuthMe();
        if (typeof nextUser.token === 'string' && nextUser.token.trim()) {
          await setStoredAuthToken(nextUser.token);
        }
        writeCachedAuthUserSnapshot(nextUser);
        setOffline(false);
        return nextUser;
      },
      staleTime: 0,
    });
    return user;
  }, [queryClient]);

  const logout = useCallback(async () => {
    // Vor dem Netzwerk-Request setzen: Der SyncProvider kann die lokale
    // Privacy-Grenze sofort und eindeutig von einem transienten Anonym-Status
    // unterscheiden.
    markExplicitLogoutPending();
    setLogoutRequested(true);
    try {
      await logoutRequest();
    } finally {
      await clearStoredAuthToken();
      clearCachedAuthUserSnapshot();
      queryClient.clear();
      // Admin-Bereich komplett isoliert raeumen: Cache + Write-Token.
      // Passiert nur bei Logout, weil /admin hier garantiert schon
      // ungemountet ist (Tab-Wechsel hatte AdminQueryScope schon geleert),
      // aber wir machen es hier explizit fuer den Fall, dass der Logout
      // aus dem /admin-Tab heraus ausgeloest wird.
      adminQueryClient.removeQueries();
      adminQueryClient.clear();
      clearAdminWriteToken();
      // OneSignal-External-ID vor dem Reload loeschen, damit das Geraet
      // beim naechsten Login nicht versehentlich dem vorherigen User
      // zugeordnet bleibt. Native-guarded + best-effort — auf Web/PWA no-op.
      await logoutOneSignal().catch(() => undefined);
      if (typeof window !== 'undefined') {
        // Harter Reload der SPA auf /onboarding -> garantiert sauberer Anonym-State
        // (kein Stale-Router-State im Capacitor-WebView; Caches/Dexie werden
        // vom SyncProvider ueber das explizite Logout-Signal geraeumt). Robuster als
        // replaceState + dispatchEvent('popstate'), das der React-Router im
        // Android-WebView nicht zuverlaessig neu bewertet.
        // DA2: replace() statt assign() — kein History-Eintrag "Onboarding", den
        // der Zurueck-Button wieder zur eingeloggten Seite fuehren wuerde.
        window.location.replace('/onboarding');
      }
    }
  }, [queryClient]);

  const dismissStartupPushPermission = useCallback(() => {
    markStartupPushPromptDone();
    setStartupPushPermissionPending(false);
  }, []);

  const requestStartupPushPermission = useCallback(async () => {
    markStartupPushPromptDone();
    setStartupPushPermissionPending(false);
    return requestPushPermission('app-hint-continue');
  }, []);

  const user = meQuery.isError && isAuthRejection(meQuery.error) ? null : meQuery.data ?? null;
  const status: AuthStatus = user
    ? 'authed'
    : meQuery.isPending || (meQuery.isError && !isAuthRejection(meQuery.error))
      ? 'loading'
      : 'anonymous';

  useEffect(() => {
    setAiDisclosureAckScope(user?.userId);
  }, [user?.userId]);

  // Beim Auth-Wechsel auf 'authed' OneSignal die Server-User-ID als External-ID
  // mitteilen — der Server zielt Push-Empfänger genau darüber an. Der
  // Helper ist selbst native-guarded (Web/PWA = no-op) und tolerant gegen
  // fehlendes Plugin, daher reicht fire-and-forget.
  useEffect(() => {
    if (status !== 'authed' || !user?.userId) return;
    // R6-02-c: Beide Umgebungen benutzen dieselbe Push-Client-Kennung, und die
    // externe Nutzerkennung ist schlicht die userId. Ein Beta-Login als Nutzer
    // 42 wuerde das Geraet an die Kennung 42 der GETEILTEN Push-Anwendung
    // binden — der Produktionsserver stellte dann Push fuer den echten
    // Prod-Nutzer 42 hier zu, mitsamt Titel, Text und Sprungziel. Im
    // Beta-Modus deshalb abmelden statt anmelden; Berechtigung und
    // Geraete-Abonnement bleiben unangetastet.
    if (runningEnv() === 'beta') {
      void logoutOneSignal().catch(() => undefined);
    } else {
      void loginOneSignal(String(user.userId)).catch(() => undefined);
    }
    // A2: Proaktiver Push-Hinweis — Standard „hoher Pace für WIEDERKEHRENDE User",
    // aber einstellbar (setProactivePushEnabled). Ein brandneuer Nutzer wird beim
    // allerersten Login NICHT sofort gefragt (erst onboarden lassen); ab der nächsten
    // Session gilt er als wiederkehrend und wird proaktiv gefragt. Wer den Dialog in
    // den Einstellungen abschaltet, bekommt ihn nur noch beim expliziten Push-Toggle.
    const returning = hasLoggedInBefore();
    markLoggedInBefore();
    if (returning && isProactivePushEnabled() && !isStartupPushPromptDone()) {
      setStartupPushPermissionPending(true);
    }
  }, [status, user?.userId]);

  const lastError = meQuery.error ?? null;

  const value = useMemo<AuthContextValue>(() => ({
    user,
    status,
    offline,
    lastError,
    logoutRequested,
    startupPushPermissionPending,
    refresh,
    login,
    logout,
    dismissStartupPushPermission,
    requestStartupPushPermission,
  }), [
    dismissStartupPushPermission,
    lastError,
    login,
    logout,
    logoutRequested,
    offline,
    refresh,
    requestStartupPushPermission,
    startupPushPermissionPending,
    status,
    user,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used within AuthProvider');
  return value;
}

export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}
